// @ts-nocheck
import * as Sentry from "@sentry/browser";
import createAuth0Client, {
  GetIdTokenClaimsOptions,
  GetTokenSilentlyOptions,
  GetTokenWithPopupOptions,
  IdToken,
  LogoutOptions,
  PopupConfigOptions,
  PopupLoginOptions,
  RedirectLoginOptions,
} from "@auth0/auth0-spa-js";
import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAnalytics } from "../lib/analytics/analytics";

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

export interface PartnerLocation {
  locationId: string;
  locationName: string;
  locationStreetAddress: string;
  locationCity: string;
}

export interface UserProfile {
  partnerName: string;
  partnerId: string;
  partnerSlug: string;
  auth0UserId: string;
  partnerLocations: PartnerLocation[];
  partnerInternalName?: string;
  payoutInUSD: boolean;
}

interface Auth0Context {
  isAuthenticated: boolean;
  user: any;
  token: string;
  userProfile?: UserProfile;
  loading: boolean;
  popupOpen: boolean;
  loginWithPopup: (
    options?: PopupLoginOptions,
    config?: PopupConfigOptions
  ) => Promise<void>;
  handleRedirectCallback: () => Promise<void>;
  getIdTokenClaims: (options?: GetIdTokenClaimsOptions) => Promise<IdToken>;
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
  getTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<any>;
  getTokenWithPopup: (
    options?: GetTokenWithPopupOptions,
    config?: PopupConfigOptions
  ) => Promise<string>;
  logout: (options?: LogoutOptions) => Promise<void>;
  setToken: React.Dispatch<React.SetStateAction<string>>;
}

const defaultAuth0Context: Auth0Context = {
  isAuthenticated: false,
  user: undefined,
  token: "",
  userProfile: undefined,
  loading: true,
  popupOpen: false,
  loginWithPopup: () => Promise.resolve(),
  handleRedirectCallback: () => Promise.resolve(),
  getIdTokenClaims: () => Promise.resolve({ __raw: "" }),
  loginWithRedirect: () => Promise.resolve(),
  getTokenSilently: () => Promise.resolve(),
  getTokenWithPopup: () => Promise.resolve(""),
  logout: () => Promise.resolve(),
  setToken: () => {},
};

export const Auth0Context = React.createContext(defaultAuth0Context);
export const useAuth0 = () => useContext(Auth0Context);

interface Auth0ProviderProps {
  children: React.ReactNode;
  onRedirectCallback: Function;
  domain: string;
  clientId: string;
  redirectUri?: string;
  audience: string;
}

function toUserProfile(data: any): UserProfile {
  return {
    partnerName: data.partner_name,
    partnerInternalName: data.partner_internal_name,
    partnerId: data.partner_id,
    partnerSlug: data.partner_slug,
    auth0UserId: data.auth0_user_id,
    partnerLocations: data.partner_locations
      ? data.partner_locations.map((pl: any) => ({
          locationId: pl.location_id,
          locationName: pl.location_name,
          locationStreetAddress: pl.location_street_address,
          locationCity: pl.location_city,
        }))
      : [],
    payoutInUSD: data.payout_in_usd,
  };
}

async function getUserProfile(authToken: string): Promise<UserProfile> {
  const response = await fetch("/api/v2/partner_user", {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
  return toUserProfile(await response.json());
}

export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  domain,
  clientId,
  redirectUri,
  audience,
}: Auth0ProviderProps) => {
  const history = useHistory();
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [userProfile, setUserProfile] = useState();
  const [token, setToken] = useState("");
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const analytics = useAnalytics();

  const trackLoginEvent = (
    id,
    email,
    name,
    partnerId,
    partnerName,
    partnerInternalName
  ) => {
    analytics.identify(id, {
      email: email,
      name: name,
    });

    analytics.screen(
      "User Login",
      {
        $email: email,
        partnerName: partnerName,
        partnerInternalName,
        partnerId: partnerId,
        pageName: "Login",
      },
      { onlyProviders: ["mixpanel"] }
    );
  };

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client({
        domain,
        client_id: clientId,
        redirect_uri: redirectUri,
        audience: audience,
        cacheLocation: "localstorage",
        useRefreshTokens: true,
      });
      setAuth0(auth0FromHook);

      if (
        window.location.search.includes("code=") &&
        window.location.search.includes("state=")
      ) {
        const { appState } = await auth0FromHook.handleRedirectCallback();
        onRedirectCallback(history, appState);
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();

      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated) {
        const user = await auth0FromHook.getUser();
        setUser(user);

        const token = await auth0FromHook.getTokenSilently();
        setToken(token);

        try {
          const userProfile = await getUserProfile(token);

          setUserProfile(userProfile);

          trackLoginEvent(
            user?.sub,
            user?.email,
            user?.nickname,
            userProfile?.partnerId,
            userProfile.partnerName,
            userProfile.partnerInternalName
          );
        } catch (error) {
          Sentry.captureException(error);
        }
      }

      setLoading(false);
    };

    initAuth0();
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);

    try {
      await auth0Client.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }

    const user = await auth0Client.getUser();
    setUser(user);
    setIsAuthenticated(true);

    const token = await auth0Client.getTokenSilently();
    setToken(token);
    const userProfile = await getUserProfile(token);
    setUserProfile(userProfile);

    setLoading(false);

    trackLoginEvent(
      user?.email,
      user?.nickname,
      userProfile?.partnerId,
      userProfile.partnerName,
      userProfile.partnerInternalName
    );
  };

  const handleRedirectCallback = async () => {
    setLoading(true);

    await auth0Client.handleRedirectCallback();

    const user = await auth0Client.getUser();
    setUser(user);

    setIsAuthenticated(true);

    const token = await auth0Client.getTokenSilently();
    setToken(token);

    const userProfile = await getUserProfile(token);
    setUserProfile(userProfile);

    setLoading(false);

    trackLoginEvent(
      user?.email,
      user?.nickname,
      userProfile?.partnerId,
      userProfile.partnerName,
      userProfile.partnerInternalName
    );
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        token,
        userProfile,
        loading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims: (options?: GetIdTokenClaimsOptions) =>
          auth0Client.getIdTokenClaims(options),
        loginWithRedirect: (options?: RedirectLoginOptions) =>
          auth0Client.loginWithRedirect(options),
        getTokenSilently: (options?: GetTokenSilentlyOptions) =>
          auth0Client.getTokenSilently(options),
        getTokenWithPopup: (
          options?: GetTokenWithPopupOptions,
          config?: PopupConfigOptions
        ) => auth0Client.getTokenWithPopup(options, config),
        logout: (options?: LogoutOptions) => auth0Client.logout(options),
        setToken,
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
